import { useState, useEffect } from 'react';
import { supabase, BlogPost } from '../../../lib/supabase';

const BRANDS = ['ロレックス', 'カルティエ', 'IWC', 'チューダー', 'ショパール', 'テンデンス'];

interface PostEditorProps {
  post: BlogPost | null;
  onSave: () => void;
  onCancel: () => void;
}

export default function PostEditor({ post, onSave, onCancel }: PostEditorProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [published, setPublished] = useState(false);
  const [brand, setBrand] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (post) {
      setTitle(post.title);
      setContent(post.content);
      setImageUrl(post.image_url || '');
      setPublished(post.published);
      setBrand(post.brand || '');
    }
  }, [post]);

  const handleSave = async () => {
    if (!title.trim() || !content.trim()) {
      alert('タイトルと本文は必須です');
      return;
    }

    setSaving(true);

    try {
      const postData = {
        title: title.trim(),
        content: content.trim(),
        image_url: imageUrl.trim() || null,
        published,
        brand: brand || null,
        updated_at: new Date().toISOString(),
      };

      if (post) {
        const { error } = await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', post.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('blog_posts')
          .insert([postData]);

        if (error) throw error;
      }

      onSave();
    } catch (error) {
      console.error('Error saving post:', error);
      alert('記事の保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">
              {post ? '記事を編集' : '新規記事作成'}
            </h1>
            <div className="flex gap-3">
              <button
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold whitespace-nowrap cursor-pointer"
              >
                キャンセル
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-2">
              タイトル <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all text-sm"
              placeholder="記事のタイトルを入力"
              required
            />
          </div>

          <div>
            <label htmlFor="brand" className="block text-sm font-semibold text-gray-700 mb-2">
              ブランド
            </label>
            <select
              id="brand"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all text-sm bg-white cursor-pointer"
            >
              <option value="">ブランドを選択（任意）</option>
              {BRANDS.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="imageUrl" className="block text-sm font-semibold text-gray-700 mb-2">
              画像URL
            </label>
            <input
              type="url"
              id="imageUrl"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all text-sm"
              placeholder="https://example.com/image.jpg"
            />
            {imageUrl && (
              <div className="mt-4 relative h-64 w-full rounded-lg overflow-hidden">
                <img
                  src={imageUrl}
                  alt="プレビュー"
                  className="w-full h-full object-cover object-top"
                  onError={(e) => {
                    e.currentTarget.src = 'https://readdy.ai/api/search-image?query=luxury%20watch%20elegant%20timepiece%20on%20sophisticated%20dark%20background%20with%20soft%20lighting%20highlighting%20craftsmanship%20and%20premium%20details&width=800&height=600&seq=blog-preview&orientation=landscape';
                  }}
                />
              </div>
            )}
          </div>

          <div>
            <label htmlFor="content" className="block text-sm font-semibold text-gray-700 mb-2">
              本文 <span className="text-red-500">*</span>
            </label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={20}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-600 focus:border-transparent transition-all text-sm font-mono"
              placeholder="記事の本文を入力（HTMLタグも使用できます）"
              required
            />
            <p className="mt-2 text-sm text-gray-500">
              HTMLタグを使用して、見出し、段落、リストなどを作成できます
            </p>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="published"
              checked={published}
              onChange={(e) => setPublished(e.target.checked)}
              className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-600 cursor-pointer"
            />
            <label htmlFor="published" className="ml-3 text-sm font-semibold text-gray-700 cursor-pointer">
              この記事を公開する
            </label>
          </div>
        </div>
      </main>
    </div>
  );
}
